import React from "react";
import { useLanguage } from "@/hooks/use-language";
import { useTournament } from "@/hooks/use-tournament";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Target, Users, UserCheck } from "lucide-react";
import { toast } from 'sonner';

interface TournamentSelectorProps {
  participants: any[];
}

export const TournamentSelector: React.FC<TournamentSelectorProps> = ({ participants }) => {
  const { t, language } = useLanguage();
  const { startTournament } = useTournament();

  // Array of realistic dummy player names
  const dummyNames = [
    "Alex Johnson", "Emma Wilson", "Michael Brown", "Sarah Davis", 
    "James Miller", "Lisa Anderson", "David Taylor", "Anna Martinez",
    "Chris Thompson", "Maria Garcia", "Robert Lee", "Jennifer White",
    "Daniel Harris", "Jessica Clark", "Matthew Lewis", "Ashley Walker",
    "Ryan Hall", "Amanda Young", "Kevin King", "Nicole Wright",
    "Brandon Scott", "Stephanie Green", "Justin Adams", "Rachel Baker"
  ];

  const generateDummyPlayer = (index: number) => ({
    id: `dummy-${index}`,
    name: dummyNames[index % dummyNames.length],
    phone: '',
    points: 0,
    matchesPlayed: 0
  });

  const handleTournamentStart = (type: 'mexicano' | 'americano' | 'team_americano') => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`Starting ${type} tournament with ${participants.length} participants`);
    }
    
    // Convert participants to Player format
    const players = participants.map((p, index) => ({
      id: p.mobil || String(index),
      name: `${p.fornavn} ${p.etternavn}`,
      phone: p.mobil,
      points: 0,
      matchesPlayed: 0
    }));
    // No upper limit - validate minimum per type
    if (type === 'mexicano') {
      if (players.length < 4) {
        toast.error(language === 'no' ? 'Trenger minst 4 spillere' : 'Need at least 4 players');
        return;
      }
    } else {
      // Americano and Team Americano: allow any >=4; bench extras to nearest multiple of 4
      if (players.length < 4) {
        toast.error(language === 'no' ? 'Trenger minst 4 spillere' : 'Need at least 4 players');
        return;
      }
    }

    startTournament(players, 0, type);
  };

  return (
    <div className="mt-8 border-t pt-6">
      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10">
          <CardTitle className="text-2xl flex items-center gap-2">
            <Trophy className="text-primary" />
            Select Tournament
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            
            {/* Mexicano Tournament */}
            <Card className="border-2 hover:border-primary/50 transition-colors">
              <CardContent className="p-6 text-center">
                <div className="mb-4">
                  <Target className="mx-auto h-12 w-12 text-blue-500" />
                </div>
                <h3 className="font-bold text-lg mb-2">
                  Mexicano
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Start a Mexicano for 4+ players (unlimited courts). Winners move up, losers move down.
                </p>
                <Button 
                  className="w-full min-h-[44px]"
                  onClick={() => handleTournamentStart('mexicano')}
                  aria-label="Start Mexicano tournament for selected players"
                >
                  Start Mexicano Tournament
                </Button>
                <div className="text-xs text-muted-foreground mt-2">
                  {participants.length} players selected
                </div>
              </CardContent>
            </Card>

            {/* Americano Tournament */}
            <Card className="border-2 hover:border-primary/50 transition-colors">
              <CardContent className="p-6 text-center">
                <div className="mb-4">
                  <Users className="mx-auto h-12 w-12 text-green-500" />
                </div>
                <h3 className="font-bold text-lg mb-2">
                  Americano
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Start an Americano tournament where players change partners each round.
                </p>
                <Button 
                  className="w-full min-h-[44px]"
                  onClick={() => handleTournamentStart('americano')}
                  aria-label="Start Americano tournament for selected players"
                >
                  Start Americano Tournament
                </Button>
                <div className="text-xs text-muted-foreground mt-2">{participants.length} players selected</div>
              </CardContent>
            </Card>

            {/* Team Americano Tournament */}
            <Card className="border-2 hover:border-primary/50 transition-colors">
              <CardContent className="p-6 text-center">
                <div className="mb-4">
                  <UserCheck className="mx-auto h-12 w-12 text-purple-500" />
                </div>
                <h3 className="font-bold text-lg mb-2">
                  Team Americano
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Start a Team Americano tournament where teams stay together throughout the tournament.
                </p>
                <Button 
                  className="w-full min-h-[44px]"
                  onClick={() => handleTournamentStart('team_americano')}
                  aria-label="Start Team Americano tournament for selected players"
                >
                  Start Team Americano Tournament
                </Button>
                <div className="text-xs text-muted-foreground mt-2">{participants.length} players selected</div>
              </CardContent>
            </Card>

          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              {participants.length} players selected
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
